/**
 * Which pairing the device's local database belongs to.
 *
 * Specification: ../test/local-owner.spec.ts.
 *
 * One database per browser origin holds the outbox, leases, verifiers, the
 * cached floor and the open checks, and none of it names a pairing. A device
 * re-paired elsewhere must start clean — but unsent events are takings, so a
 * change of pairing is refused while any remain.
 */

import type { SqliteDriver } from "@gf/kernel/sqlite";

export interface LocalIdentity {
  readonly tenantId: string;
  readonly venueId: string;
  readonly deviceId: string;
}

export type LocalOwnership =
  | { readonly kind: "CLAIMED" }
  | { readonly kind: "SAME" }
  | { readonly kind: "WIPED"; readonly previous: LocalIdentity }
  | { readonly kind: "BLOCKED"; readonly previous: LocalIdentity; readonly unsent: number };

interface OwnerRow {
  tenant_id: string;
  venue_id: string;
  device_id: string;
}

const same = (a: LocalIdentity, b: LocalIdentity): boolean =>
  a.tenantId === b.tenantId && a.venueId === b.venueId && a.deviceId === b.deviceId;

export async function scopeLocalData(driver: SqliteDriver, identity: LocalIdentity): Promise<LocalOwnership> {
  await driver.exec(`
    CREATE TABLE IF NOT EXISTS local_owner (
      k         INTEGER PRIMARY KEY CHECK (k = 1),
      tenant_id TEXT    NOT NULL,
      venue_id  TEXT    NOT NULL,
      device_id TEXT    NOT NULL,
      since     INTEGER NOT NULL
    )
  `);
  const [row] = await driver.all<OwnerRow>("SELECT tenant_id, venue_id, device_id FROM local_owner WHERE k = 1", []);
  if (!row) {
    await setOwner(driver, identity);
    return { kind: "CLAIMED" };
  }
  const previous: LocalIdentity = { tenantId: row.tenant_id, venueId: row.venue_id, deviceId: row.device_id };
  if (same(previous, identity)) return { kind: "SAME" };

  const tables = await localTables(driver);
  if (tables.some((t) => t.name === "outbox")) {
    const [unsent] = await driver.all<{ n: number }>("SELECT COUNT(*) AS n FROM outbox", []);
    if ((unsent?.n ?? 0) > 0) return { kind: "BLOCKED", previous, unsent: unsent!.n };
  }

  await wipe(driver, tables, identity);
  return { kind: "WIPED", previous };
}

/** Every unsent outbox row, raw — for the device to hand over before a take-over. */
export async function unsentEvents(driver: SqliteDriver): Promise<Record<string, unknown>[]> {
  const tables = await localTables(driver);
  if (!tables.some((t) => t.name === "outbox")) return [];
  return driver.all<Record<string, unknown>>("SELECT * FROM outbox ORDER BY seq, event_id", []);
}

/**
 * Wipe unsent events too and belong to `identity`. Only after the screen has
 * handed the events over (`unsentEvents`) and somebody confirmed.
 */
export async function takeOverLocalData(driver: SqliteDriver, identity: LocalIdentity): Promise<void> {
  await wipe(driver, await localTables(driver), identity);
}

const localTables = (driver: SqliteDriver): Promise<{ name: string }[]> =>
  driver.all<{ name: string }>(
    "SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%' AND name <> 'local_owner'",
    [],
  );

async function wipe(driver: SqliteDriver, tables: readonly { name: string }[], identity: LocalIdentity): Promise<void> {
  await driver.exec("BEGIN IMMEDIATE");
  try {
    for (const { name } of tables) await driver.exec(`DELETE FROM "${name.replaceAll('"', '""')}"`);
    await setOwner(driver, identity);
    await driver.exec("COMMIT");
  } catch (error) {
    await driver.exec("ROLLBACK");
    throw error;
  }
}

async function setOwner(driver: SqliteDriver, identity: LocalIdentity): Promise<void> {
  await driver.run(
    `INSERT INTO local_owner (k, tenant_id, venue_id, device_id, since) VALUES (1, ?, ?, ?, ?)
     ON CONFLICT (k) DO UPDATE SET tenant_id = excluded.tenant_id, venue_id = excluded.venue_id,
       device_id = excluded.device_id, since = excluded.since`,
    [identity.tenantId, identity.venueId, identity.deviceId, Date.now()],
  );
}
